import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import {
  Popover,
  PopoverAnchor,
  PopoverContent
} from "@/components/ui/popover";
import { FieldSourceCard } from "../fieldSource/FieldSourceCard";
import type {
  FieldSourceItem,
  OpenFormCitation,
  ResolveFieldSourceUser
} from "../fieldSource/types";

/** How long the pointer rests on a field before its card opens. */
export const HOVER_CARD_OPEN_MS = 300;
/** Grace for moving the pointer from the field onto its card. */
const HOVER_CARD_CLOSE_MS = 150;

/**
 * Which field's card shows: the hovered one (after a rest), else the one
 * keyboard focus is on, else the pinned one. Hovering another field while a
 * card is open moves it without the delay.
 */
export function useFieldHoverCard({
  enabled,
  hasCard
}: {
  enabled: boolean;
  hasCard: (fieldName: string) => boolean;
}) {
  const [hovered, setHovered] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const hoveredRef = useRef<string | null>(null);
  hoveredRef.current = hovered;

  const clearTimer = (timer: React.MutableRefObject<number | null>) => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };

  const scheduleClose = useCallback(() => {
    clearTimer(openTimer);
    clearTimer(closeTimer);
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      setHovered(null);
    }, HOVER_CARD_CLOSE_MS);
  }, []);

  const keepOpen = useCallback(() => clearTimer(closeTimer), []);

  const hoverField = useCallback(
    (fieldName: string | null) => {
      if (!enabled) return;
      clearTimer(openTimer);
      if (!fieldName || !hasCard(fieldName)) {
        if (hoveredRef.current) scheduleClose();
        return;
      }
      clearTimer(closeTimer);
      if (hoveredRef.current) {
        setHovered(fieldName);
        return;
      }
      openTimer.current = window.setTimeout(() => {
        openTimer.current = null;
        setHovered(fieldName);
      }, HOVER_CARD_OPEN_MS);
    },
    [enabled, hasCard, scheduleClose]
  );

  const focusField = useCallback(
    (fieldName: string) => {
      if (enabled && hasCard(fieldName)) setFocused(fieldName);
    },
    [enabled, hasCard]
  );

  const blurField = useCallback(() => setFocused(null), []);

  const togglePin = useCallback(
    (fieldName: string) => {
      setPinned((current) =>
        current === fieldName || !hasCard(fieldName) ? null : fieldName
      );
    },
    [hasCard]
  );

  const close = useCallback(() => {
    clearTimer(openTimer);
    clearTimer(closeTimer);
    setHovered(null);
    setFocused(null);
    setPinned(null);
  }, []);

  useEffect(() => {
    if (!enabled) close();
  }, [close, enabled]);

  useEffect(
    () => () => {
      clearTimer(openTimer);
      clearTimer(closeTimer);
    },
    []
  );

  const shown = hovered ?? focused ?? pinned;
  return {
    shown,
    pinned: shown !== null && shown === pinned,
    hoverField,
    keepOpen,
    scheduleClose,
    focusField,
    blurField,
    togglePin,
    close
  };
}

type FieldHoverCardLayerProps = {
  item: FieldSourceItem | null;
  pinned: boolean;
  /** The field's box on screen now; read on every reposition (scroll, zoom). */
  getAnchorRect: () => DOMRect | null;
  /** Pointer presses here leave the card to the page's own click handling. */
  isOwnTarget: (target: EventTarget | null) => boolean;
  resolveUser?: ResolveFieldSourceUser;
  openCitationFieldName?: string | null;
  onToggleCitation?: (citation: OpenFormCitation) => void;
  onOpenSource?: () => void;
  onComment?: () => void;
  onEditValue?: () => void;
  onClose: () => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
};

/**
 * The hover variant of `FieldSourceCard`, anchored to a form field. The anchor
 * is virtual (the field's live rectangle), so the card follows the page as it
 * scrolls and flips at the pane's edge. Esc and a press elsewhere close it.
 */
export function FieldHoverCardLayer({
  item,
  pinned,
  getAnchorRect,
  isOwnTarget,
  resolveUser,
  openCitationFieldName,
  onToggleCitation,
  onOpenSource,
  onComment,
  onEditValue,
  onClose,
  onPointerEnter,
  onPointerLeave
}: FieldHoverCardLayerProps) {
  const lastRect = useRef(new DOMRect());
  const getAnchorRectRef = useRef(getAnchorRect);
  getAnchorRectRef.current = getAnchorRect;
  const anchorRef = useMemo(
    () => ({
      current: {
        getBoundingClientRect: () => {
          const rect = getAnchorRectRef.current();
          if (rect) lastRect.current = rect;
          return lastRect.current;
        }
      }
    }),
    []
  );

  return (
    <Popover
      open={Boolean(item)}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <PopoverAnchor virtualRef={anchorRef} />
      {item ? (
        <PopoverContent
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          hideWhenDetached
          className="z-10000 w-auto gap-0 bg-transparent p-0 shadow-none ring-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            if (isOwnTarget(event.target)) event.preventDefault();
          }}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
        >
          <FieldSourceCard
            variant="hover"
            item={item}
            pinned={pinned}
            onClose={onClose}
            resolveUser={resolveUser}
            openCitationFieldName={openCitationFieldName}
            onToggleCitation={onToggleCitation}
            onOpenSource={onOpenSource}
            onComment={onComment}
            onEditValue={onEditValue}
          />
        </PopoverContent>
      ) : null}
    </Popover>
  );
}
